/**
 * Frontend Entry Point
 * React application with TypeScript and Vite
 */

// Validate env first so a misconfigured deploy fails loudly on boot rather
// than mid-render. Side-effect import: ./config/env throws on failure.
import "./config/env"

import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import React from "react"
import ReactDOM from "react-dom/client"
import App from "./App"
import "./assets/styles/index.css" // Single source of truth for all styles

// Initialize Sentry in staging/production environments only
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.MODE === "production") {
  // Parse the trace-sample env explicitly so that an intentional `0`
  // (sampling off for a quiet investigation) isn't silently coerced to
  // the 0.1 default by the `||` truthiness fallback.
  const parsedSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
  const tracesSampleRate =
    Number.isFinite(parsedSampleRate) && parsedSampleRate >= 0 && parsedSampleRate <= 1
      ? parsedSampleRate
      : 0.1

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === "true",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate,
  })
}

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
})

// Error fallback component for Sentry ErrorBoundary
interface ErrorFallbackProps {
  error: unknown
  resetError: () => void
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const errorMessage = error instanceof Error ? error.message : "An unknown error occurred"

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-muted-foreground">{errorMessage}</p>
        <button
          onClick={resetError}
          className="mt-4 rounded-md bg-primary px-4 py-2 text-primary-foreground"
        >
          Try again
        </button>
      </div>
    </div>
  )
}

const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error("Failed to find the root element")
}
ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
)
